// Agent-based step-by-step simulator.
//
// Given a World (shared reality for one trial) and a routing mode, walk the
// process step by step, accumulating wall-clock and human-hours.
//
// Both modes ("naive" = human-routed today; "reroute" = with Planapus) consume
// the SAME pre-rolled per-person dice from the World, so when they happen to
// route a step to the same person they get the same outcome. Variance between
// the two paths comes from real routing/AI differences, not Monte Carlo noise.

import { MODE_NAIVE, MODE_REROUTE, SimMode, pickAssignee } from "./sim-router";
import type { ProcessDef, ProcessStep } from "./processes";
import type { World } from "./sim-world";

export { MODE_NAIVE, MODE_REROUTE };
export type { SimMode };

// --------------------------------------------------------------------------
// Per-step Planapus efficiency band — applied to the REROUTE path.
//
// MIN_PLANAPUS_GAIN: the floor. Even on steps where AI never runs (e.g.
//   AI-ineligible legal redlines, in-person inspections), Planapus's
//   orchestration layer reduces cognitive load: fewer fields to re-verify,
//   prefilled context, less paperwork to chase. Modeled as a flat
//   per-step efficiency gain in REROUTE mode regardless of automation.
//
// MIN_REVIEW_PCT: the ceiling. When AI does handle a step, a human still
//   reviews at least 5% of the original workHrs — both a quality guard
//   and a model-realism guard against claiming a step is 100% automated.
//   This caps the per-step efficiency gain at 95%.
// --------------------------------------------------------------------------
const MIN_PLANAPUS_GAIN = 0.10;
const MIN_REVIEW_PCT = 0.05;

// Compute which steps AI may handle. Steps with `aiEligible: true` rank
// first; the knob picks the top N of them. Used by both the engine (here)
// and exported via simulation.js for callers that want to inspect coverage.
export function computeEligibleMask(proc: ProcessDef, automationPct: number): boolean[] {
  const indexed = proc.steps.map((s, i) => ({ i, eligible: s.aiEligible ? 1 : 0 }));
  indexed.sort((a, b) => (b.eligible - a.eligible) || (a.i - b.i));
  const N = Math.round(automationPct * proc.steps.length);
  const picked = new Set(indexed.slice(0, N).map(x => x.i));
  return proc.steps.map((_, i) => picked.has(i));
}

export interface TraceEntry extends ProcessStep {
  ai: boolean;
  lat: number;
  atHours: number;
  assigneeName: string;
  assigneeOrgId: string;
  pickupHr: number;
  dropped?: boolean;
  waitHrs?: number;
  touchHrs?: number;
}

export interface SimulateOnceResult {
  hours: number;
  humanHours: number;
  failed: boolean;
  trace: TraceEntry[];
}

export interface SimulateOnceOpts {
  recordTrace?: boolean;
  /**
   * When true and mode === reroute, AI may also DO the work on aiEligible
   * steps (not just route). Default: true when mode is reroute, false when
   * naive.
   */
  aiPath?: boolean;
  /** Override of which AI-eligible steps the AI actually handles. */
  automationPct?: number;
  /** Per-eligible-step coin flip of "AI actually did it" (0..1). */
  aiCoverage?: number;
}

export function simulateOnce(
  world: World,
  mode: SimMode,
  opts: SimulateOnceOpts = {},
): SimulateOnceResult {
  const proc = world.proc;
  const params = world.params || {};
  const recordTrace = !!opts.recordTrace;
  const aiPath = opts.aiPath ?? (mode === MODE_REROUTE);
  const automationPct = opts.automationPct ?? params.automationPct ?? 0.6;
  const aiCoverage = opts.aiCoverage ?? params.aiCoverage ?? 0.9;

  // Which steps AI may handle (sorted: aiEligible:true first).
  const eligibleMask = computeEligibleMask(proc, automationPct);

  let nowHr = 0;
  let humanHours = 0;
  let failed = false;
  const trace: TraceEntry[] = [];

  for (let i = 0; i < proc.steps.length; i++) {
    const step = proc.steps[i];

    // Decide whether AI directly handles the work on this step.
    const aiHandled =
      aiPath && step.aiEligible && eligibleMask[i] && hashUnit(world.seed, i) < aiCoverage;

    if (aiHandled) {
      // AI doesn't queue on anyone's calendar — near-instant pickup, plus a
      // human-review tail proportional to the step's complexity. The catalog
      // can override with step.aiReviewPct (0..1); otherwise we use a
      // heuristic based on workHrs.
      //
      // CEILING: max 95% efficiency gain per step. Even an AI that does
      // ~all the touch work, a human spends at least 5% of the original
      // workHrs reviewing the output — both as a quality check and a
      // model-realism guard (the simulator should never claim a step is
      // 100% automatable to zero human time).
      const lat = 0.05 + (((world.seed + i * 13) % 100) / 100) * 0.2; // 3–15 min
      const explicit = step.aiReviewPct;
      const reviewPct = explicit != null
        ? Math.max(MIN_REVIEW_PCT, Math.min(1, explicit))
        : Math.max(MIN_REVIEW_PCT, defaultAiReviewPct(step.workHrs));
      const work = step.workHrs * reviewPct;
      nowHr += lat;
      humanHours += work;
      if (recordTrace) {
        trace.push({
          ...step,
          ai: true,
          lat,
          atHours: nowHr,
          assigneeName: "Planapus",
          assigneeOrgId: "ai",
          pickupHr: nowHr - lat,
        });
      }
      continue;
    }

    // Otherwise: pick a human in the step's `to` party.
    const { agent, pickupHr } = pickAssignee(world, step.to, nowHr, mode);
    if (!agent || !isFinite(pickupHr)) {
      // No-one available in the simulation window — count as a failure.
      failed = true;
      break;
    }

    // Wait time (queueing) is pickup minus arrival; work time is what the
    // person actually spends. Both consume wall clock.
    const waitHrs = pickupHr - nowHr;
    // Dice derived on demand from (worldSeed, agent.idNum, stepIdx). Same
    // (seed, agent, step) always yields the same draws — so both NAIVE and
    // REROUTE simulations get identical outcomes when they route the same
    // step to the same person.
    const diceDrop    = hashUnit(world.seed, agent.idNum * 11 + i * 3 + 1);
    const diceError   = hashUnit(world.seed, agent.idNum * 11 + i * 3 + 2);
    const diceLatency = hashGauss(world.seed, agent.idNum * 11 + i * 3 + 3);

    // Drop (the person fumbles it altogether). Same dice for both paths if
    // both routed here.
    if (diceDrop < agent.dropRate) {
      failed = true;
      if (recordTrace) {
        trace.push({
          ...step,
          ai: false,
          lat: waitHrs,
          atHours: pickupHr,
          assigneeName: agent.name,
          assigneeOrgId: agent.orgId,
          pickupHr,
          dropped: true,
        });
      }
      break;
    }

    // Total in-desk work split into two components:
    //   queueHrs  — "even when at my desk, my queue depth means I don't
    //               start your thing immediately." Centered on agent.latencyMu
    //               × 0.5, lognormal-spread by their personal sigma.
    //   touchHrs  — actual hands-on work for THIS step (step.workHrs),
    //               stretched by the agent's loadFactor.
    const queueCenter = Math.max(0.5, agent.latencyMu * 0.5);
    const queueHrs = Math.max(0.1, Math.exp(Math.log(queueCenter) + agent.latencySigma * diceLatency));
    const stepWorkHrs = Math.max(0.05, step.workHrs);
    let workHrs = queueHrs + stepWorkHrs * agent.loadFactor;
    let stepLaborHrs = stepWorkHrs;

    // Error → rework adds ~70% of touch back, plus extra hands-on time.
    if (diceError < agent.errorRate) {
      workHrs += stepWorkHrs * 0.7 * agent.loadFactor;
      stepLaborHrs += stepWorkHrs * 0.5;
    }

    // FLOOR: even when Planapus doesn't actually run the step, the
    // orchestrator layer reduces cognitive load — fewer fields to verify,
    // less paperwork to chase, less complexity to keep track of. We model
    // this as a flat per-step efficiency gain that applies in REROUTE mode
    // (the "with Planapus" path) regardless of AI handling. Without this,
    // a process with automationPct=0 makes Planapus look indistinguishable
    // from today, which is wrong — workflow orchestration is real value
    // even before any AI actually does work.
    if (mode === MODE_REROUTE) {
      const assist = 1 - MIN_PLANAPUS_GAIN;
      workHrs *= assist;
      stepLaborHrs *= assist;
    }

    humanHours += stepLaborHrs;
    const touchHrs = workHrs;

    // Advance wall clock by walking forward in the person's working calendar
    // — they only work during their available hours.
    const finishHr = agent.calendar.advanceWorkingHours(pickupHr, touchHrs);
    if (!isFinite(finishHr)) {
      // Step couldn't finish within the simulation window. Treat as a
      // failure rather than letting Infinity escape into hours[]. (Without
      // this guard, a non-finite hours field propagates to the histogram's
      // Math.max → infinite loop → browser OOM.)
      failed = true;
      if (recordTrace) {
        trace.push({
          ...step, ai: false, lat: pickupHr - nowHr, atHours: pickupHr,
          assigneeName: agent.name, assigneeOrgId: agent.orgId, pickupHr,
          dropped: true,
        });
      }
      break;
    }
    nowHr = finishHr;
    // (humanHours was already incremented above with the Planapus-assist
    // multiplier applied — don't double-count step.workHrs here.)

    if (recordTrace) {
      trace.push({
        ...step,
        ai: false,
        lat: waitHrs + (finishHr - pickupHr),
        atHours: finishHr,
        assigneeName: agent.name,
        assigneeOrgId: agent.orgId,
        pickupHr,
        waitHrs,
        touchHrs: finishHr - pickupHr,
      });
    }
  }

  return { hours: nowHr, humanHours, failed, trace };
}

// Deterministic uniform in [0,1) derived from (seed, index). Used both for
// the "does AI handle this step?" coin and for per-(agent, step) dice in
// the simulator core. Replaces the per-trial prerolls Map.
function hashUnit(seed: number, idx: number): number {
  let h = (seed * 0x9E3779B1 + idx * 0x85EBCA77) | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85EBCA77);
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE3D);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// AI human-review tail as a fraction of the step's authored workHrs, used
// when the catalog hasn't set `aiReviewPct` explicitly. Trivial sends/
// requests need near-zero review; substantive drafts need real oversight.
//
//   < 0.5h   → 10%  (forwards, requests, schedule a thing, fire a webhook)
//   < 2h     → 20%  (form prefills, simple validation, status emails)
//   < 5h     → 35%  (drafts, summaries, light analyses)
//   ≥ 5h     → 50%  (substantive deliverables — long SIG, deep extract)
function defaultAiReviewPct(workHrs: number): number {
  if (!isFinite(workHrs) || workHrs < 0.5) return 0.10;
  if (workHrs < 2)   return 0.20;
  if (workHrs < 5)   return 0.35;
  return 0.50;
}

// Deterministic standard-normal draw via Box-Muller on two derived uniforms.
function hashGauss(seed: number, idx: number): number {
  const u = 1 - hashUnit(seed, idx * 2 + 1);
  const v = hashUnit(seed, idx * 2 + 2);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
